import { type IllustrationType } from "@clipboard-health/ui-components";

import { WORKER_OUTCOME_RESULT, type WorkerOutcomeResult } from "../../api/useUpdateInterview";
import { FEEDBACK_STEP, type FeedbackStep } from "./feedbackStep";

export interface FreeTextContext {
  result: WorkerOutcomeResult;
  textFieldHeading: string;
  textFieldPlaceholder?: string;
  parentStep: FeedbackStep;
  confirmationStep: FeedbackStep;
}

interface OptionItem {
  label: string;
  value: WorkerOutcomeResult;
  textFieldHeading?: string;
  textFieldPlaceholder?: string;
}

export const GOT_OFFER_SUB_OPTIONS: OptionItem[] = [
  {
    label: "Yes, I know the next steps",
    value: WORKER_OUTCOME_RESULT.gotOfferKnowNextSteps,
  },
  {
    label: "They said they'd follow up",
    value: WORKER_OUTCOME_RESULT.gotOfferFollowUpNextSteps,
  },
  {
    label: "No, they didn't explain next steps",
    value: WORKER_OUTCOME_RESULT.gotOfferNoNextSteps,
  },
];

export const NOT_HEARD_BACK_SUB_OPTIONS: OptionItem[] = [
  {
    label: "In a few days or less",
    value: WORKER_OUTCOME_RESULT.notHeardBackFewDays,
  },
  {
    label: "Within a week",
    value: WORKER_OUTCOME_RESULT.notHeardBackWithinWeek,
  },
  {
    label: "They didn't specify",
    value: WORKER_OUTCOME_RESULT.notHeardBackNotSpecified,
  },
];

export const NOT_OFFERED_ROLE_OPTIONS: OptionItem[] = [
  {
    label: "Schedule misalignment",
    value: WORKER_OUTCOME_RESULT.scheduleMisalignment,
  },
  {
    label: "Pay misalignment",
    value: WORKER_OUTCOME_RESULT.payMisalignment,
  },
  {
    label: "Not enough experience",
    value: WORKER_OUTCOME_RESULT.notEnoughExperience,
  },
  {
    label: "They didn't give a reason",
    value: WORKER_OUTCOME_RESULT.notSure,
  },
  {
    label: "Other",
    value: WORKER_OUTCOME_RESULT.notOfferTheRoleOther,
    textFieldHeading: "Share more details (Optional)",
  },
];

export const NOT_INTERVIEW_OPTIONS: OptionItem[] = [
  {
    label: "They are no longer hiring",
    value: WORKER_OUTCOME_RESULT.noLongerHiring,
  },
  {
    label: "Interviewer unavailable",
    value: WORKER_OUTCOME_RESULT.interviewNotAvailable,
  },

  {
    label: "Other",
    value: WORKER_OUTCOME_RESULT.notInterviewOther,
    textFieldHeading: "Please share why you didn't interview (Optional)",
    textFieldPlaceholder: "Example: I got there too late and was turned away",
  },
];

export const CONFIRMATION_CONFIGS: Record<
  string,
  { title: string; description: string; illustration?: IllustrationType; toJobsPath?: boolean }
> = {
  [FEEDBACK_STEP.gotOffer]: {
    title: "Thanks for letting us know",
    description: "We'll use this to improve communication after interviews.",
  },
  [FEEDBACK_STEP.notHeardBack]: {
    title: "Thanks for letting us know",
    description: "We'll check back with you within a week.",
    toJobsPath: true,
  },
  [FEEDBACK_STEP.notOfferConfirmation]: {
    title: "Thanks for letting us know",
    description: "We're sorry it didn't work out this time.",
    toJobsPath: true,
  },
  [FEEDBACK_STEP.notInterviewConfirmation]: {
    title: "Thanks for letting us know",
    description: "We're working to prevent this from happening again.",
    toJobsPath: true,
  },
};

export const OPTION_STEP_CONFIGS: Record<
  string,
  {
    options: OptionItem[];
    heading: string;
    subtitle: string | undefined;
    nextStep: FeedbackStep;
    freeTextConfirmationStep?: FeedbackStep;
  }
> = {
  [FEEDBACK_STEP.gotOfferSubOptions]: {
    options: GOT_OFFER_SUB_OPTIONS,
    heading: "Great news! What's next?",
    subtitle: "Did they walk you through the next steps to get started?",
    nextStep: FEEDBACK_STEP.gotOffer,
  },
  [FEEDBACK_STEP.notHeardBackSubOptions]: {
    options: NOT_HEARD_BACK_SUB_OPTIONS,
    heading: "When did they say they'd follow up?",
    subtitle: "Let us know what timeline they shared, so we know when to check back.",
    nextStep: FEEDBACK_STEP.notHeardBack,
  },
  [FEEDBACK_STEP.notOfferTheRole]: {
    options: NOT_OFFERED_ROLE_OPTIONS,
    heading: "Why wasn't it a match?",
    subtitle: "Select the main reason.",
    nextStep: FEEDBACK_STEP.notOfferConfirmation,
    freeTextConfirmationStep: FEEDBACK_STEP.notOfferConfirmation,
  },
  [FEEDBACK_STEP.notInterview]: {
    options: NOT_INTERVIEW_OPTIONS,
    heading: "What happened?",
    subtitle: "We're sorry to hear that you couldn't interview.",
    nextStep: FEEDBACK_STEP.notInterviewConfirmation,
    freeTextConfirmationStep: FEEDBACK_STEP.notInterviewConfirmation,
  },
};
